import { Pin, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { RoutingSkuTargetRecord } from '@nadoutong/contracts';
import { capabilityStatusLabel } from '../../pages/capabilityStatus.ts';
import { parseNonNegativeInteger, parseOptionalRetryOrder } from '../../pages/tenantRoutingShared.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Checkbox, Chip, IconButton, InfoHint, Input, Td, Tr } from '../../ui/index.ts';
import { useToast } from '../Toast.tsx';
import { SUPPLY_ID_CELL_CLASS } from './SupplyTableHead.tsx';
import type { BoundSupplyRow as BoundRowModel, BoundRowIssue } from './supplyRows.ts';

/**
 * One already-bound supply row (#1428).
 *
 * The controls are drafts, not writes: weight / retry order / enabled edit
 * locally and only reach the server on 「保存」. The previous pool table
 * hid these behind a dialog, and the obvious merge — commit on change —
 * would turn a mistyped digit in a dense table into a live traffic shift on
 * every keystroke. A dirty row says so.
 *
 * On an UNPINNED row those same controls are disabled. `parsePutTarget`
 * rejects an empty `capability_id`, so there is no request this row could
 * send: the only write available to it is the pin, and the pin submits the
 * SERVER's weight/retry/enabled, not a draft. Accepting keystrokes here would
 * let an operator zero out a weight, watch the row go dirty, pin it, and be
 * told it succeeded while the lane keeps taking traffic at the old weight.
 */

interface Draft {
  readonly weight: string;
  readonly retryOrder: string;
  readonly enabled: boolean;
}

function draftFrom(target: RoutingSkuTargetRecord): Draft {
  return {
    weight: String(target.weight),
    retryOrder: target.retry_order === null ? '' : String(target.retry_order),
    enabled: target.enabled,
  };
}

export interface BoundRowSubmission {
  readonly weight: number;
  readonly retry_order: number | null;
  readonly enabled: boolean;
}

function issueLabelKey(issue: BoundRowIssue): I18nKey {
  switch (issue) {
    case 'legacy_unpinned':
      return 'sku_detail.supply.issue.legacy_unpinned';
    case 'not_in_candidates':
      return 'sku_detail.supply.issue.not_in_candidates';
    case 'probe_abnormal':
      return 'sku_detail.supply.issue.probe_abnormal';
  }
}

export function BoundSupplyRow({
  row,
  saving,
  onSave,
  onDelete,
  onPin,
}: {
  readonly row: BoundRowModel;
  readonly saving: boolean;
  readonly onSave: (target: RoutingSkuTargetRecord, submission: BoundRowSubmission) => void;
  readonly onDelete: (row: BoundRowModel) => void;
  readonly onPin: (target: RoutingSkuTargetRecord) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { target, candidate, issue } = row;
  const [draft, setDraft] = useState<Draft>(() => draftFrom(target));

  // A refetch that lands while the row is untouched should show the server's
  // truth; one that lands mid-edit must not eat the operator's keystrokes.
  // Keying the reset on the SERVER values (not the draft) gives exactly that.
  useEffect(() => {
    setDraft(draftFrom(target));
  }, [target.weight, target.retry_order, target.enabled, target.id]);

  const saved = draftFrom(target);
  const dirty = draft.weight !== saved.weight ||
    draft.retryOrder !== saved.retryOrder ||
    draft.enabled !== saved.enabled;
  // `parsePutTarget` rejects a null/empty `capability_id` outright, so a
  // legacy row cannot be saved in place at all — it has to be pinned first,
  // which is also why its tuning inputs are read-only.
  const pinnable = target.capability_id === null;
  const upstreamLabel = candidate?.upstream_name ?? target.upstream_id;
  const identityLine = candidate === null
    ? `${target.account_id} · ${target.key_bucket_id}`
    : `${target.upstream_id} · ${target.account_id} · ${target.key_bucket_id}`;
  const upstreamModelLabel = candidate?.model ?? target.upstream_model_name ?? '—';
  const capabilityLabel = target.capability_id ?? t('sku_detail.supply.no_capability_pin');

  function handleSave(): void {
    const weight = parseNonNegativeInteger(draft.weight);
    if (weight === null) {
      toast.error(t('tenant_routing.error.default_weight'));
      return;
    }
    const retryOrder = parseOptionalRetryOrder(draft.retryOrder);
    if (retryOrder === undefined) {
      toast.error(t('tenant_routing.error.retry_order'));
      return;
    }
    onSave(target, { weight, retry_order: retryOrder, enabled: draft.enabled });
  }

  return (
    <Tr data-testid={`sku-supply-bound-row-${target.id}`}>
      <Td>
        {/*
          The targets endpoint carries no `upstream_name`, so a row with no
          candidate falls back to the id — and then the id must not also head
          the sub-line, or the cell reads as a stutter (`up_xai` over
          `up_xai · acc_xai · kb_xai`). The sub-line drops whatever the main
          line already said.
        */}
        <div className={SUPPLY_ID_CELL_CLASS}>
          <span className="block truncate text-paper" title={upstreamLabel}>
            {upstreamLabel}
          </span>
          <span
            className="mt-0.5 block truncate font-mono text-micro text-paper-low"
            title={identityLine}
          >
            {identityLine}
          </span>
        </div>
      </Td>
      <Td mono>
        <div className={SUPPLY_ID_CELL_CLASS}>
          <span className="block truncate text-paper" title={target.upstream_channel_slug}>
            {target.upstream_channel_slug}
          </span>
          <span
            className="mt-0.5 block truncate text-micro text-paper-low"
            title={target.upstream_channel_id}
          >
            {target.upstream_channel_id}
          </span>
        </div>
      </Td>
      <Td mono>
        {/*
          Capped like the other two identifier columns: this one carries a
          36-char `capability_id`, so once 上游 / 上游渠道 stopped stretching it
          became the last id column still sized by its content — the same
          inert-`truncate` failure #1442 diagnosed, one column over.
        */}
        <div className={SUPPLY_ID_CELL_CLASS}>
          {/*
            The candidate's `model` is the same capability column the candidate
            rows below render; `upstream_model_name` is a DIFFERENT column of
            the capability table. Preferring the candidate keeps one column
            meaning one thing across the table, and falls back only when this
            target has no candidate to read from.
          */}
          <span className="block truncate text-paper" title={upstreamModelLabel}>
            {upstreamModelLabel}
          </span>
          <span className="mt-0.5 block truncate text-micro text-paper-low" title={capabilityLabel}>
            {capabilityLabel}
          </span>
          {/*
            Two xai rows can share upstream, account, key bucket AND upstream
            model, differing only by lane and protocol. Without the protocol
            they read as duplicates.
          */}
          {target.capability_protocol !== null ? (
            <span
              className="mt-0.5 block truncate text-micro text-paper-low"
              title={target.capability_protocol}
              data-testid={`sku-supply-protocol-${target.id}`}
            >
              {target.capability_protocol}
            </span>
          ) : null}
        </div>
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          {/*
            `capabilityStatusLabel` maps a null status to 「pinned capability is
            no longer live」, which is right for a row that HAS a pin the chain
            lost — and flatly wrong for one that never had a pin at all. On a
            legacy row it put three contradictory claims on one screen: this
            cell saying the pin died, the chip beside it saying there is no
            pin, and the header popover saying route liveness is fine. An
            unpinned row simply has no probe verdict to report.
          */}
          <span data-testid={`sku-supply-probe-${target.id}`}>
            {target.capability_id === null
              ? t('sku_detail.supply.probe_unpinned')
              : capabilityStatusLabel(target.capability_status, t)}
          </span>
          {issue !== null ? (
            <span className="inline-flex items-center gap-1">
              <Chip tone="warn" lamp={false} testId={`sku-supply-issue-${target.id}`}>
                {t(issueLabelKey(issue))}
              </Chip>
              {issue === 'legacy_unpinned' ? (
                <InfoHint
                  label={t('sku_detail.hint.label')}
                  content={t('sku_detail.hint.legacy_unpinned')}
                  testId={`sku-supply-issue-hint-${target.id}`}
                />
              ) : null}
            </span>
          ) : null}
        </div>
      </Td>
      <Td num>
        {/*
          The targets endpoint does not carry a procurement multiplier — only
          the candidate read does. So with no candidate we do not know the
          cost, and 「not priced」 would be a claim we cannot support: on the
          real `xai/grok-4.5` (candidates always empty) it labelled two lanes
          unpriced while `upstream_channel_model_costs` held 0.7 for both, and
          the readiness popover on the same screen said every bound target has
          an explicit multiplier. Unknown is unknown.
        */}
        <span data-testid={`sku-supply-bound-multiplier-${target.id}`}>
          {candidate === null
            ? t('sku_detail.supply.multiplier_unknown')
            : candidate.cost_multiplier === null
              ? t('sku_detail.supply.multiplier_missing')
              : String(candidate.cost_multiplier)}
        </span>
      </Td>
      <Td>
        {/*
          Weight, retry order and enabled share ONE cell (#1428 round 5).
          As three separate columns the table needed ~1200px of content width;
          at 1440 — an ordinary laptop — the two number inputs were squeezed to
          41px and their values became invisible, which broke the single
          capability this merge exists to provide. They belong together anyway:
          all three are the same decision about how this lane is scheduled.
        */}
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center justify-end gap-2 text-micro text-paper-low">
            <span className="whitespace-nowrap">{t('sku_detail.supply.column.weight')}</span>
            <Input
              type="number"
              min={0}
              step={1}
              mono
              aria-label={t('sku_detail.supply.column.weight')}
              className="h-8 px-2 text-right"
              wrapperClassName="w-[4.5rem] shrink-0"
              disabled={pinnable}
              value={draft.weight}
              onChange={(event) => {
                const weight = event.currentTarget.value;
                setDraft((current) => ({ ...current, weight }));
              }}
              data-testid={`sku-supply-weight-${target.id}`}
            />
          </label>
          <label className="flex items-center justify-end gap-2 text-micro text-paper-low">
            <span className="whitespace-nowrap">{t('tenant_routing.pool.retry_order')}</span>
            <Input
              type="number"
              min={1}
              step={1}
              mono
              aria-label={t('tenant_routing.pool.retry_order')}
              placeholder={t('tenant_routing.pool.retry_order_auto')}
              className="h-8 px-2 text-right"
              wrapperClassName="w-[4.5rem] shrink-0"
              disabled={pinnable}
              value={draft.retryOrder}
              onChange={(event) => {
                const retryOrder = event.currentTarget.value;
                setDraft((current) => ({ ...current, retryOrder }));
              }}
              data-testid={`sku-supply-retry-order-${target.id}`}
            />
          </label>
          <span className="flex items-center justify-end gap-2 text-micro text-paper-low">
            <span className="whitespace-nowrap">{t('sku_detail.supply.column.enabled')}</span>
            <Checkbox
              checked={draft.enabled}
              disabled={pinnable}
              onCheckedChange={(enabled) => setDraft((current) => ({ ...current, enabled }))}
              testId={`sku-supply-enabled-${target.id}`}
            />
          </span>
        </div>
      </Td>
      <Td className="text-right">
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {dirty ? (
            <Chip tone="warn" lamp={false} testId={`sku-supply-dirty-${target.id}`}>
              {t('sku_detail.supply.unsaved')}
            </Chip>
          ) : null}
          {pinnable ? (
            <span className="inline-flex flex-col items-end gap-1">
              <Button
                size="sm"
                disabled={saving}
                onClick={() => onPin(target)}
                data-testid={`sku-supply-pin-${target.id}`}
              >
                <Pin aria-hidden="true" className="h-3.5 w-3.5" />
                {t('sku_detail.supply.pin')}
              </Button>
              <span
                className="text-right text-micro text-paper-low"
                data-testid={`sku-supply-pin-first-${target.id}`}
              >
                {t('sku_detail.supply.pin_before_tuning')}
              </span>
            </span>
          ) : (
            <Button
              size="sm"
              disabled={saving || !dirty}
              onClick={handleSave}
              data-testid={`sku-supply-save-${target.id}`}
            >
              {t('sku_detail.supply.save')}
            </Button>
          )}
          <IconButton
            size="sm"
            label={t('tenant_routing.pool.remove_target')}
            disabled={saving}
            onClick={() => onDelete(row)}
            className="text-bad hover:text-bad"
            data-testid={`sku-supply-delete-${target.id}`}
          >
            <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </Td>
    </Tr>
  );
}
